import { useMemo, useState } from "react"
import { claimLadderStage } from "@/api/homeMotivationApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import {
  HomeLadderMotivation,
  HomeMotivationStatus,
} from "@/types/homeMotivation"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

type Props = {
  ladder: HomeLadderMotivation
  onUpdated: (status: HomeMotivationStatus) => void
}

export default function GuardianLadderCard({ ladder, onUpdated }: Props) {
  const { applyPassport, refreshPassport } = useGuardiansPassport()
  const [collapsed, setCollapsed] = useState(false)
  const [claiming, setClaiming] = useState(false)

  const current = useMemo(() => {
    if (ladder.currentStageKey) {
      return ladder.stages.find((s) => s.key === ladder.currentStageKey) ?? null
    }
    return ladder.stages.find((s) => s.unlocked && !s.claimed) ?? null
  }, [ladder])

  const onClaim = async () => {
    if (!current?.claimable) return
    setClaiming(true)
    try {
      const result = await claimLadderStage(current.key)
      onUpdated(result.status)
      applyPassport({
        coins: result.coins,
        chakraCharges: result.chakraCharges,
      })
      await refreshPassport()
      window.alert(
        `${result.title}\nТитул получен · +${result.reward.coins} монет · +${result.reward.chakra} чакры`
      )
    } catch (e) {
      window.alert(getGameApiErrorMessage(e, "Не удалось забрать"))
    } finally {
      setClaiming(false)
    }
  }

  if (!current && ladder.stages.every((s) => s.claimed)) {
    return (
      <div className="rounded-2xl border border-violet-400/30 bg-violet-400/[0.07] p-3.5">
        <div className="text-[15px] font-extrabold text-white">Путь хранителя</div>
        <div className="mt-1 text-sm font-bold text-green-400">
          {ladder.activeTitle
            ? `Титул: ${ladder.activeTitle}`
            : "Все этапы пройдены"}
        </div>
      </div>
    )
  }

  if (!current) return null

  const progressPct = Math.min(
    100,
    (current.doneCount / Math.max(1, current.requiredTasks)) * 100
  )

  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-violet-400/30 bg-violet-400/[0.07] p-3.5">
      <button
        type="button"
        className="flex w-full flex-row items-center gap-2.5 text-left"
        onClick={() => setCollapsed((v) => !v)}
      >
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide text-violet-300">
            Путь хранителя
          </div>
          <div className="text-[15px] font-extrabold text-white">
            {current.title}
            {ladder.activeTitle ? ` · ${ladder.activeTitle}` : ""}
          </div>
          <div className="text-xs font-semibold text-white/55">
            {current.doneCount}/{current.requiredTasks} задач
            {current.totalTasks > current.requiredTasks
              ? ` (из ${current.totalTasks})`
              : ""}
          </div>
        </div>
        <span className="text-white/45">{collapsed ? "▼" : "▲"}</span>
      </button>

      <div className="h-2 overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-violet-400"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {!collapsed && (
        <div className="flex flex-col gap-2">
          {current.tasks.map((task) => (
            <div key={task.key} className="flex flex-row items-start gap-2">
              <span
                className={`mt-0.5 shrink-0 text-base ${task.done ? "text-green-400" : "text-white/35"}`}
              >
                {task.done ? "✓" : "○"}
              </span>
              <span
                className={`text-sm ${task.done ? "text-white/45 line-through" : "text-white/85"}`}
              >
                {task.title}
                {task.target != null && task.progress != null
                  ? ` (${task.progress}/${task.target})`
                  : ""}
              </span>
            </div>
          ))}
          <div className="flex flex-row flex-wrap gap-1.5">
            {ladder.stages.map((stage) => (
              <span
                key={stage.key}
                className={`rounded-lg px-2 py-1 text-[10px] font-bold ${
                  stage.claimed
                    ? "bg-green-500/20 text-green-300"
                    : stage.key === current.key
                      ? "bg-violet-500/30 text-white"
                      : "bg-white/10 text-white/60"
                }`}
              >
                {stage.title}
              </span>
            ))}
          </div>
        </div>
      )}

      {current.claimable ? (
        <button
          type="button"
          disabled={claiming}
          onClick={() => void onClaim()}
          className="rounded-xl bg-guardians-accent py-2.5 text-sm font-extrabold text-guardians-bg disabled:opacity-60"
        >
          {claiming
            ? "…"
            : `Забрать «${current.title}» · +${current.rewardCoins}`}
        </button>
      ) : null}
    </div>
  )
}
